import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    Box,
    Stack,
    FormControl,
    FormLabel,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { axios } from '@/services/axios';
import {
    Button,
    Heading,
    Input,
    LoadingIndicator,
    Text,
} from '@/components/v4';
import FieldToolTip from '@/components/forms/new/FieldToolTip';
import {
    formLabelStyling,
    stackSpacing,
    stackStyling,
    textBoxStyling,
    breadCrumbStyling,
    headingStyling,
} from '@/components/forms/new/formStyles';
import DropDown from '@/components/forms/new/formikFormFields/DropDown';
import { statusToString } from '../utils';

const EditOCTrunk = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(true);
    const [trunkName, setTrunkName] = useState('');
    const [trunkData, setTrunkData] = useState(null);
    const [callingProfileList, setCallingProfileList] = useState([]);
    const [defaultCallingProfile, setDefaultCallingProfile] = useState('');
    const [isHoveredName, setIsHoveredName] = useState(false);
    const [isHoveredProfile, setIsHoveredProfile] = useState(false);

    const { darkMode } = useSelector((state) => state.settings);
    const { currentCompany } = useSelector((state) => state.navigationLists);

    const handleBackbutton = () => {
        navigate('/trunks');
        dispatch({
            type: 'CHANGE_TABLE_LOADING',
        });
    };

    // loading trunk api call
    useEffect(() => {
        if (id) {
            axios
                .get(`/Trunk/${id}`)
                .then((res) => {
                    setTrunkData(res.data);
                    setTrunkName(res.data.name || '');
                    setCallingProfileList(res.data.callingProfileList || []);
                    setDefaultCallingProfile(
                        res.data.defaultOperatorConnectCallingProfile || '',
                    );
                    setIsLoading(false);
                })
                .catch((err) => {
                    toast.error(
                        err?.response?.data?.message || 'Failed to load trunk',
                    );
                    setIsLoading(false);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // reset navigation on company change
    useEffect(() => {
        if (trunkData?.companyID && trunkData.companyID !== currentCompany) {
            navigate('/trunks');
        }
    }, [trunkData, currentCompany, navigate]);

    // editing a trunk api call
    const handleEditTrunk = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            await axios.post(
                '/trunk/name',
                {
                    trunkId: trunkData.id,
                    trunkName: trunkName,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                },
            );
            await axios.post(
                '/trunk/defaultcallingprofile',
                {
                    trunkId: trunkData.id,
                    operatorConnectCallingProfileID: defaultCallingProfile,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                },
            );
            toast.success('Trunk updated successfully');
            navigate('/trunks');
            dispatch({
                type: 'CHANGE_TABLE_LOADING',
            });
        } catch (err) {
            toast.error(
                err?.response?.data?.message || 'Failed to update trunk',
            );
            setIsLoading(false);
        }
    };

    const breadCrumbTrail = [
        { label: 'Trunks', path: '/trunks' },
        { label: 'Edit Trunk', path: '' },
    ];

    const handleDefaultCallingProfileChange = (item) => {
        setDefaultCallingProfile(item?.id || '');
    };

    if (isLoading) {
        return <LoadingIndicator />;
    }

    return (
        <>
            <Breadcrumb sx={breadCrumbStyling}>
                {breadCrumbTrail?.map((crumb, index) => (
                    <BreadcrumbItem key={index}>
                        <BreadcrumbLink
                            as={Link}
                            to={crumb.path}
                            onClick={
                                crumb.path === '/trunks'
                                    ? () =>
                                          dispatch({
                                              type: 'CHANGE_TABLE_LOADING',
                                          })
                                    : undefined
                            }>
                            {crumb.label}
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                ))}
            </Breadcrumb>

            <Heading sx={headingStyling}>
                {trunkData?.sipHeader || trunkData?.name}
            </Heading>

            <Box as="form" onSubmit={handleEditTrunk}>
                <Box
                    borderRadius={15}
                    padding="30px"
                    maxWidth={895}
                    ml={0}
                    bg={darkMode ? 'dark.tcap' : 'white'}
                    border="1px solid"
                    borderColor={
                        darkMode ? 'dark.tcap-borderGray' : 'gray.200'
                    }>
                    {trunkData?.status !== undefined && (
                        <FormControl sx={{ margin: '0 !important', mb: 4 }}>
                            <Stack
                                direction="row"
                                spacing={stackSpacing}
                                sx={stackStyling()}>
                                <FormLabel
                                    sx={formLabelStyling(darkMode, true)}>
                                    Status
                                </FormLabel>
                                <Box flex={1} maxWidth="400px">
                                    <Text>
                                        {statusToString(trunkData.status)}
                                    </Text>
                                </Box>
                            </Stack>
                        </FormControl>
                    )}

                    <FormControl
                        sx={{ margin: '0 !important', mb: 4 }}
                        onMouseEnter={() => setIsHoveredName(true)}
                        onMouseLeave={() => setIsHoveredName(false)}>
                        <Stack
                            direction="row"
                            spacing={stackSpacing}
                            sx={stackStyling()}>
                            <FormLabel sx={formLabelStyling(darkMode, true)}>
                                Trunk Name
                            </FormLabel>
                            <Box flex={1} maxWidth="400px">
                                <Input
                                    type="text"
                                    id="trunkName"
                                    name="trunkName"
                                    value={trunkName}
                                    onChange={(e) =>
                                        setTrunkName(e.target.value)
                                    }
                                    sx={textBoxStyling}
                                    placeholder="Enter trunk name"
                                />
                            </Box>
                            {isHoveredName && (
                                <FieldToolTip
                                    label="The display name for this trunk"
                                    ariaLabel="trunk-name-tip"
                                    left="25px"
                                    top="8px"
                                />
                            )}
                        </Stack>
                    </FormControl>

                    <FormControl
                        sx={{ margin: '0 !important', mb: 4 }}
                        onMouseEnter={() => setIsHoveredProfile(true)}
                        onMouseLeave={() => setIsHoveredProfile(false)}>
                        <Stack
                            direction="row"
                            spacing={stackSpacing}
                            sx={{ ...stackStyling(), height: 'auto' }}>
                            <FormLabel sx={formLabelStyling(darkMode, true)}>
                                Default Calling Profile
                            </FormLabel>
                            <Box flex={1} maxWidth="400px">
                                <DropDown
                                    field={{ value: defaultCallingProfile }}
                                    data={callingProfileList || []}
                                    shownValue="name"
                                    selectedId="id"
                                    name="defaultCallingProfile"
                                    currentId={defaultCallingProfile}
                                    handleSelectChange={
                                        handleDefaultCallingProfileChange
                                    }
                                    placeholder="- Select Calling Profile -"
                                    darkMode={darkMode}
                                    noPadding
                                    width="100%"
                                />
                            </Box>
                            {isHoveredProfile && (
                                <FieldToolTip
                                    label="The default calling profile for this trunk"
                                    ariaLabel="calling-profile-tip"
                                    left="25px"
                                    top="10px"
                                />
                            )}
                        </Stack>
                    </FormControl>
                </Box>

                <Box sx={{ marginTop: '25px', display: 'flex', gap: 2 }}>
                    <Button
                        variant="outline"
                        sx={{ background: 'white', minWidth: '120px' }}
                        onClick={handleBackbutton}>
                        Back
                    </Button>
                    <Button
                        type="submit"
                        sx={{ minWidth: '120px' }}
                        isLoading={isLoading}>
                        Update
                    </Button>
                </Box>
            </Box>
        </>
    );
};

export default EditOCTrunk;
